import React, { useState, useMemo } from 'react';
import { Icon } from 'src/components/atoms/Icon';
import { Button } from 'src/components/atoms/Button';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { useAppContext } from 'src/contexts/AppContext';
import { GoalDrawer } from 'src/features/Goals/components/molecules/GoalDrawer';
import { useCreateGoalMutation } from 'src/api/goals';
import { useCampaignGroupsQuery } from 'src/api/campaignGroups';
import { useUsersQuery } from 'src/api/users';
import { CreateGoalRequest } from 'src/features/Goals/types';

export const OverviewAddGoalButton: React.FC = () => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName ?? '';
  const { toastState, showSuccess, showError, hideToast } = useCustomToast();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data: campaignGroups = [] } = useCampaignGroupsQuery(customerId);
  const { data: users = [] } = useUsersQuery(customerId);
  const createGoalMutation = useCreateGoalMutation(customerId);

  const campaignGroupsForDrawer = useMemo(
    () => campaignGroups.map((g) => ({ id: g.id, name: g.groupName })),
    [campaignGroups],
  );

  const usersForDrawer = useMemo(
    () =>
      users.map((u) => ({
        id: u.id,
        name: u.displayName ?? u.email,
        avatarUrl: undefined,
      })),
    [users],
  );

  const handleOpenDrawer = () => setIsDrawerOpen(true);
  const handleCloseDrawer = () => setIsDrawerOpen(false);

  const handleCreateSubmit = async (data: CreateGoalRequest) => {
    try {
      await createGoalMutation.mutateAsync(data);
      showSuccess('Goal created', 'Your goal has been created successfully.');
      handleCloseDrawer();
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || 'Unable to create the goal. Please try again.';
      showError('Create failed', message);
    }
  };

  return (
    <>
      <Button
        buttonName="add-new-goal"
        variant="outline"
        borderColor="icon.green"
        color="text.title"
        bg="surface.card"
        borderRadius="8px"
        fontFamily="marketingPage"
        px={3}
        py={2}
        minH="40px"
        w={{ base: '100%', sm: 'auto' }}
        onClick={handleOpenDrawer}
        _hover={{
          bg: 'surface.greenHover',
          borderColor: 'green.600',
        }}>
        <Icon iconName="plus" w={4} h={4} mr={2} color="text.title" />
        Add New Goal
      </Button>

      <GoalDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        onCreateSubmit={handleCreateSubmit}
        onUpdateSuccess={handleCloseDrawer}
        onUpdateError={(message) => showError('Update failed', message ?? '')}
        isCreateLoading={createGoalMutation.isLoading}
        editingGoal={null}
        customer={customerId}
        campaignGroups={campaignGroupsForDrawer}
        users={usersForDrawer}
      />

      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        duration={toastState.duration}
        onClose={hideToast}
      />
    </>
  );
};
